// Generated by GitHub Copilot

import { LogLevel } from '@azure/msal-browser';

/**
 * Reads a config value from window.__APP_CONFIG__ (injected by config.js at
 * runtime — tokens replaced by Azure DevOps). Falls back to process.env for
 * local development where config.js tokens are not substituted.
 *
 * @param {string} runtimeKey  - Key on window.__APP_CONFIG__
 * @param {string} envKey      - Fallback process.env key (REACT_APP_*)
 * @returns {string}
 */
function cfg(runtimeKey, envKey) {
  const runtimeVal = window.__APP_CONFIG__?.[runtimeKey];
  // Treat unsubstituted Azure DevOps placeholders (#{...}) as absent
  if (runtimeVal && !runtimeVal.startsWith('#{')) return runtimeVal;
  return process.env[envKey] || '';
}

/**
 * Reads a boolean config flag from window.__APP_CONFIG__ (or REACT_APP_* env
 * fallback). Runtime values may be a real boolean (from config.js) or the
 * strings 'true'/'false'; env values are always strings. Unsubstituted
 * Azure DevOps placeholders (#{...}) are treated as absent.
 *
 * @param {string} runtimeKey  - Key on window.__APP_CONFIG__
 * @param {string} envKey      - Fallback process.env key (REACT_APP_*)
 * @param {boolean} defaultVal - Value used when unset/unsubstituted
 * @returns {boolean}
 */
function cfgBool(runtimeKey, envKey, defaultVal) {
  const runtimeVal = window.__APP_CONFIG__?.[runtimeKey];
  // Real boolean from config.js
  if (typeof runtimeVal === 'boolean') return runtimeVal;
  // String value from config.js (ignore unsubstituted #{...} placeholders)
  if (typeof runtimeVal === 'string' && !runtimeVal.startsWith('#{')) {
    return runtimeVal.toLowerCase() === 'true';
  }
  const envVal = process.env[envKey];
  if (typeof envVal === 'string' && envVal.length > 0) {
    return envVal.toLowerCase() === 'true';
  }
  return defaultVal;
}

/**
 * Whether Microsoft Entra ID / MSAL SSO is enforced.
 *
 * Source: window.__APP_CONFIG__.sso (config.js runtime token), falling back to
 * REACT_APP_SSO (.env). Default = true (SSO enabled) when unset/unsubstituted,
 * preserving the app's secure-by-default behaviour.
 *
 * When false: MSAL is never initialised, no <MsalProvider> is mounted, all
 * routes are directly accessible, and no login/avatar button renders.
 */
export const ssoEnabled = cfgBool('sso', 'REACT_APP_SSO', true);

/**
 * MSAL configuration for Microsoft Entra ID (Azure AD) SSO.
 *
 * In production: values come from window.__APP_CONFIG__ (config.js),
 * whose #{...} tokens are replaced by Azure DevOps at release time.
 *
 * In local dev: values fall back to REACT_APP_* variables in .env.
 *
 * Auth Code + PKCE flow is used automatically by MSAL for browser SPAs.
 * Tokens are stored in sessionStorage (clears on tab close — reduces XSS risk).
 *
 * Security: Never put client secrets in browser code.
 */
export const msalConfig = {
  auth: {
    clientId:                  cfg('clientId',   'REACT_APP_AZURE_CLIENT_ID'),
    authority:                 `https://login.microsoftonline.com/${cfg('tenantId', 'REACT_APP_AZURE_TENANT_ID')}`,
    redirectUri:               cfg('redirectUri', 'REACT_APP_REDIRECT_URI') || window.location.origin,
    postLogoutRedirectUri:     window.location.origin,
    navigateToLoginRequestUrl: window.__APP_CONFIG__?.navigateToLoginRequestUrl ?? false,
  },
  cache: {
    cacheLocation:        'sessionStorage', // sessionStorage clears on tab close — reduces XSS exposure window
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        if (process.env.NODE_ENV !== 'production') {
          switch (level) {
            case LogLevel.Error:   console.error('[MSAL]', message); break;
            case LogLevel.Warning: console.warn('[MSAL]', message);  break;
            case LogLevel.Info:    console.info('[MSAL]', message);  break;
            case LogLevel.Verbose: console.debug('[MSAL]', message); break;
            default: break;
          }
        }
      },
      piiLoggingEnabled: false,
      logLevel: process.env.NODE_ENV === 'production' ? LogLevel.Error : LogLevel.Warning,
    },
    allowNativeBroker: false,
  },
};

/**
 * Scopes for the initial login request.
 */
export const loginRequest = {
  scopes: ['openid', 'profile', 'email', 'User.Read'],
};

/**
 * Scopes for acquiring an access token to call a protected back-end API.
 * Populate REACT_APP_API_SCOPE in .env (or add apiScope to config.js) when needed.
 *
 * Note: the Client Stories API does NOT use this — it validates the MSAL
 * id_token (see src/auth/acquireApiToken.js), which needs no custom API scope.
 */
export const apiRequest = {
  scopes: [process.env.REACT_APP_API_SCOPE].filter(Boolean),
};
